<template>
	<Modal
		class="custom-btn-form"
		:title="title"
		@close="close"
	>
		<template #body>
			<div class="custom-btn-form--body">
				<div class="details form-section">
					<h3 class="section-title">
						{{ transUcfirst('commons.details') }}
					</h3>
					<div class="row">
						<div class="col">
							<FormField
								:label="transUcfirst('commons.name')"
								:error="formErrors.name"
								class="name-field"
							>
								<div class="form-input form-input--with-icon">
									<input
										ref="nameField"
										v-model="name"
										type="text"
									>
									<Icon
										icon-name="pen"
										size="md"
									/>
								</div>
							</FormField>
						</div>
						<div class="col">
							<FormField
								:label="transUcfirst('commons.preview')"
								class="preview-field"
							>
								<TagButton
									icon-name="tag-general_tagging"
									:label="name"
									:clickable="false"
								/>
							</FormField>
						</div>
					</div>
				</div>
				<div class="duration form-section">
					<div class="section-title">
						<h3>{{ transUcfirst('tag_group.clip_duration') }}</h3>
						<div
							v-tooltip="{content: clipDurationInfo, classes: 'tooltip'}"
							class="duration-info"
						>
							<Icon
								icon-name="c-info"
								size="sm"
							/>
						</div>
					</div>
					<div class="row">
						<div class="col">
							<FormField
								:label="transUcfirst('tag_group.preroll')"
							>
								<DurationInput v-model="duration.preRoll" />
							</FormField>
						</div>
						<div class="col">
							<FormField
								:label="transUcfirst('tag_group.postroll')"
							>
								<DurationInput v-model="duration.postRoll" />
							</FormField>
						</div>
					</div>
				</div>
			</div>
		</template>
		<template #footer-buttons>
			<div class="row">
				<div class="col" />
				<div class="col">
					<button
						:disabled="!canSave"
						class="button button--responsive button--primary h-width-full"
						@click="saveForm"
					>
						{{ transUcfirst('commons.save') }}
					</button>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@components/modal/Modal';
import FormField from '@videotools/js/one/components/Form/FormField';
import TagButton from '@videotools/js/one/components/TagButton/TagButton';
import DurationInput from '@videotools/js/one/components/TagGroups/DurationInput';
import { validators } from '@videotools/js/form/form-validator';
import FormValidationMixin from '@videotools/js/mixins/form-validation';
import TagButtonEntity from '@videotools/js/api/entities/TagButton';

export default {
	name:       'CustomButtonForm',
	components: { DurationInput, TagButton, FormField, Modal },
	mixins:     [FormValidationMixin],
	props:      {
		title: {
			type:     String,
			required: true,
		},
		tagButton: {
			type:    TagButtonEntity,
			default: () => new TagButtonEntity(),
		},
	},
	data() {
		return {
			buttonId: null,
			name:     '',
			duration: {
				preRoll:  5,
				postRoll: 5,
			},
		};
	},
	computed: {
		clipDurationInfo() {
			return `${this.transUcfirst('tag_group.tag_duration_info.preroll')}<br/>${this.transUcfirst('tag_group.tag_duration_info.postroll')}`;
		},
		canSave() {
			return !this.hasFormErrors;
		},
	},
	created() {
		this.initFormData();
		this.initValidator({
			name: {
				fn:    validators.required,
				error: this.transUcfirst('validation.required', { attribute: this.transUcfirst('validation.attributes.name') }),
			},
		}, {
			name: this.name,
		});
		this.$watch('name', this.onNameChange);
	},
	mounted() {
		this.$refs.nameField.focus();
	},
	methods: {
		initFormData() {
			this.buttonId = this.tagButton.id;
			this.name = this.tagButton.name;
			this.duration.preRoll = this.tagButton.secondsBefore ?? this.duration.preRoll;
			this.duration.postRoll = this.tagButton.secondsAfter ?? this.duration.postRoll;
		},
		saveForm() {
			this.formErrors = this.formValidator.validateAll().errors;
			if (!this.formValidator.hasErrors) {
				this.$emit('save',
					{
						id:       this.buttonId,
						name:     this.name,
						duration: this.duration,
					}
				);
			}
		},
		onNameChange(value) {
			this.validateField('name', value);
		},
		close() {
			this.$emit('close');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.custom-btn-form {
	&--body {
		.form-section {
			margin-bottom: 40px;
		}
	}

	.section-title {
		margin-bottom: 12px;
	}

	::v-deep {
		.modal--content.desktop {
			width: auto;
		}

		.form-field--label {
			display: block;
		}

		.modal--footer {
			border-top: 1px solid var(--color-grey-800);
		}
	}

	.row {
		display: flex;

		>:first-child {
			margin-right: 24px;
		}
	}

	.col {
		flex: 1;
	}

	.name-field, .preview-field {
		width: 204px;
	}

	.preview-field::v-deep .tag-button {
		background-color: var(--color-grey-800);
		border-color: var(--color-grey-850);

		.shadow {
			background: linear-gradient(270deg, var(--color-grey-800) 0%, rgba(56, 57, 61, 0) 100%)
		}
	}

	input[type=text] {
		width: 100%;
	}

	.duration {
		.section-title {
			display: flex;
			align-items: center;
			cursor: pointer;

			>:first-child {
				margin-right: 8px;
			}
		}
	}
}

</style>
